import {
  ICustomFieldValueForCreate,
  ITasksInputCreate,
} from '@/api/types/request.types'
import {
  IStatusForSelection,
  IPriorityLevelForSelection,
  IPersonWithEmail,
  ICustomField,
} from '@/api/types/response.types'
import CustomFieldAssistant, {
  ECustomFieldNumberTypes,
  ECustomFieldTextTypes,
  ECustomFieldTypes,
} from '@/helpers/custom-field-assistant'

export interface ICsvTaskRow {
  title: string
  description: string
  status: string
  priority: string
  assignee?: string
  dueDate?: string
  tags?: string
  customFieldValues: ICustomFieldValueForCreate[]
}

/* A CSV column holding the values of one custom field */
export interface ICsvCustomFieldColumn {
  field: ICustomField
  index: number
}

export interface ICsvRowError {
  row: number
  field: string
  message: string
}

export interface ICsvParseResult {
  headers: string[]
  rows: string[][]
}

export interface ICsvValidationResult {
  validRows: ICsvTaskRow[]
  errors: ICsvRowError[]
  warnings: string[]
}

export interface ICsvImportOptions {
  listStatuses: IStatusForSelection[]
  listPriorities: IPriorityLevelForSelection[]
  listAssignees: IPersonWithEmail[]
  listCustomFields: ICustomField[]
}

const REQUIRED_COLUMNS = ['title', 'status', 'priority']
const OPTIONAL_COLUMNS = ['description', 'assignee', 'due_date', 'tags']
const ALL_COLUMNS = [...REQUIRED_COLUMNS, ...OPTIONAL_COLUMNS]

/*
 * Other header names read as a standard column. Exports title the due date column "Due Date", so an
 * exported file's due dates come back in, and a custom field named due_date keeps its own column.
 */
const COLUMN_ALIASES: Record<string, string[]> = {
  due_date: ['due date'],
}

const DATE_FORMATS = 'YYYY-MM-DD, MM/DD/YYYY, or MMM D, YYYY'
const MONTH_ABBREVIATIONS = [
  'jan', 'feb', 'mar', 'apr', 'may', 'jun',
  'jul', 'aug', 'sep', 'oct', 'nov', 'dec',
]

const CHECKBOX_CHECKED_VALUES = ['yes', 'true', '1']
const CHECKBOX_UNCHECKED_VALUES = ['no', 'false', '0']

/* Dropdown format notes list this many options before summarizing the rest */
const MAX_OPTIONS_IN_FORMAT = 5

const normalizeColumnName = (name: string): string => name.toLowerCase().trim()

/* The header index each standard column reads from: the first header with any of its names, or -1 */
const findStandardColumnIndexes = (headers: string[]): Record<string, number> => {
  const indexes: Record<string, number> = {}
  for (const col of ALL_COLUMNS) {
    const names = [col, ...(COLUMN_ALIASES[col] || [])]
    indexes[col] = headers.findIndex(h => names.includes(h))
  }
  return indexes
}

export default class CsvImportAssistant {
  /**
   * Parses raw CSV text into headers and rows.
   * Handles quoted fields containing commas and newlines.
   */
  public static parseCsvText(text: string): ICsvParseResult {
    const rows: string[][] = []
    let current = ''
    let inQuotes = false
    let fields: string[] = []

    for (let i = 0; i < text.length; i++) {
      const char = text[i]
      const nextChar = text[i + 1]

      if (inQuotes) {
        if (char === '"' && nextChar === '"') {
          // Escaped quote
          current += '"'
          i++
        } else if (char === '"') {
          inQuotes = false
        } else {
          current += char
        }
      } else {
        if (char === '"') {
          inQuotes = true
        } else if (char === ',') {
          fields.push(current.trim())
          current = ''
        } else if (char === '\n' || char === '\r') {
          fields.push(current.trim())
          current = ''
          if (fields.some(f => f !== '')) {
            rows.push(fields)
          }
          fields = []
          if (char === '\r' && nextChar === '\n') {
            i++ // skip \n in \r\n
          }
        } else {
          current += char
        }
      }
    }

    // Handle last field/row
    fields.push(current.trim())
    if (fields.some(f => f !== '')) {
      rows.push(fields)
    }

    if (rows.length === 0) {
      return { headers: [], rows: [] }
    }

    const headers = rows[0].map(h => h.toLowerCase().trim())
    return { headers, rows: rows.slice(1) }
  }

  /**
   * Validates that the CSV structure has all required columns, and finds the columns
   * named after custom fields the requester can edit. The rows are only read to tell
   * whether an uneditable field's column holds anything that will be dropped.
   */
  public static validateCsvStructure(
    headers: string[],
    rows: string[][],
    customFields: ICustomField[] = [],
    canEditField: (field: ICustomField) => boolean = () => true,
  ): {
    isValid: boolean
    errors: string[]
    warnings: string[]
    customFieldColumns: ICsvCustomFieldColumn[]
  } {
    const errors: string[] = []
    const warnings: string[] = []
    const standardIndexes = findStandardColumnIndexes(headers)

    for (const col of REQUIRED_COLUMNS) {
      if (standardIndexes[col] === -1) {
        errors.push(`Missing required column: "${col}"`)
      }
    }

    const matchedColumns = this.matchCustomFieldColumns(
      headers,
      customFields,
      Object.values(standardIndexes),
      warnings,
    )
    const customFieldColumns = matchedColumns.filter(c => {
      if (canEditField(c.field)) {
        return true
      }
      // The example file has a blank column for every visible field, view-only ones included
      if (rows.some(row => (row[c.index] || '').trim() !== '')) {
        warnings.push(
          `You can't edit the custom field "${c.field.name}", so its values will be ignored.`,
        )
      }
      return false
    })

    const unknownColumns = headers.filter(
      (h, idx) =>
        !Object.values(standardIndexes).includes(idx) &&
        !matchedColumns.some(c => c.index === idx),
    )
    if (unknownColumns.length > 0) {
      warnings.push(
        `Unknown columns will be ignored: ${unknownColumns.join(', ')}`,
      )
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      customFieldColumns,
    }
  }

  /**
   * Pairs custom fields with the columns named after them, in field order.
   * Standard columns claim their headers first (see findStandardColumnIndexes).
   * Exports put the custom field columns after the standard ones, one per field
   * in field order, so the remaining headers with a shared name are paired from
   * the last column backwards.
   */
  private static matchCustomFieldColumns(
    headers: string[],
    customFields: ICustomField[],
    standardIndexes: number[],
    warnings: string[],
  ): ICsvCustomFieldColumn[] {
    const columns: ICsvCustomFieldColumn[] = []
    const names = Array.from(
      new Set(customFields.map(f => normalizeColumnName(f.name))),
    )

    for (const name of names) {
      const fields = customFields.filter(
        f => normalizeColumnName(f.name) === name,
      )
      const namedIndexes = headers
        .map((h, idx) => (h === name ? idx : -1))
        .filter(idx => idx !== -1)
      const indexes = namedIndexes.filter(idx => !standardIndexes.includes(idx))

      if (namedIndexes.length > 0 && indexes.length === 0) {
        warnings.push(
          `The "${name}" column fills in the task's own ${name}, not the custom field with the same name.`,
        )
      }

      const count = Math.min(fields.length, indexes.length)
      for (let i = 1; i <= count; i++) {
        columns.push({
          field: fields[fields.length - i],
          index: indexes[indexes.length - i],
        })
      }
    }

    return columns.sort(
      (a, b) => customFields.indexOf(a.field) - customFields.indexOf(b.field),
    )
  }

  /**
   * Validates each row and returns typed task rows plus any errors.
   */
  public static validateCsvRows(
    headers: string[],
    rows: string[][],
    customFieldColumns: ICsvCustomFieldColumn[] = [],
  ): ICsvValidationResult {
    const validRows: ICsvTaskRow[] = []
    const errors: ICsvRowError[] = []
    const warnings: string[] = []

    const standardIndexes = findStandardColumnIndexes(headers)

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i]
      const rowNum = i + 2 // +1 for 0-index, +1 for header row
      let rowValid = true

      const getValue = (col: string): string => {
        const idx = standardIndexes[col]
        if (idx === -1 || idx >= row.length) {
          return ''
        }
        return row[idx].trim()
      }

      const title = getValue('title')
      const description = getValue('description')
      const status = getValue('status')
      const priority = getValue('priority')
      const assignee = getValue('assignee')
      const dueDate = getValue('due_date')
      const tags = getValue('tags')

      if (!title) {
        errors.push({ row: rowNum, field: 'title', message: 'Title is required' })
        rowValid = false
      }

      if (!status) {
        errors.push({ row: rowNum, field: 'status', message: 'Status is required' })
        rowValid = false
      }

      if (!priority) {
        errors.push({
          row: rowNum,
          field: 'priority',
          message: 'Priority is required',
        })
        rowValid = false
      }

      const parsedDueDate = dueDate ? this.parseDate(dueDate) : null
      if (dueDate && !parsedDueDate) {
        errors.push({
          row: rowNum,
          field: 'due_date',
          message: `Due date must be in ${DATE_FORMATS} format`,
        })
        rowValid = false
      }

      const customFieldValues: ICustomFieldValueForCreate[] = []
      for (const column of customFieldColumns) {
        const raw = column.index < row.length ? row[column.index].trim() : ''
        const result = this.parseCustomFieldValue(column.field, raw)
        if (result.error) {
          errors.push({
            row: rowNum,
            field: column.field.name,
            message: result.error,
          })
          rowValid = false
        } else if (result.value) {
          customFieldValues.push(result.value)
        }
      }

      if (rowValid) {
        validRows.push({
          title,
          description,
          status,
          priority,
          assignee: assignee || undefined,
          dueDate: parsedDueDate || undefined,
          tags: tags || undefined,
          customFieldValues,
        })
      }
    }

    return { validRows, errors, warnings }
  }

  /**
   * Reads one custom field cell. Blank cells and unchecked boxes mean no value, like
   * leaving the field empty on the create form. Anything the field could not store
   * is an error, using the same rules as the create form.
   */
  public static parseCustomFieldValue(
    field: ICustomField,
    raw: string,
  ): { value?: ICustomFieldValueForCreate; error?: string } {
    if (!raw) {
      return {}
    }

    const value = { customFieldID: field.id } as ICustomFieldValueForCreate

    switch (field.customFieldType) {
      case ECustomFieldTypes.Text: {
        const error = CustomFieldAssistant.validateText(field, raw)
        if (error) {
          return { error }
        }
        value.textData = raw
        return { value }
      }

      case ECustomFieldTypes.Number: {
        const number = this.stripNumberFormatting(field, raw)
        const error = CustomFieldAssistant.validateNumber(field, number)
        if (error) {
          return { error }
        }
        value.numberData = Number(number)
        return { value }
      }

      case ECustomFieldTypes.Dropdown: {
        const option = (field.options || []).find(
          o => o.name.toLowerCase().trim() === raw.toLowerCase(),
        )
        if (!option) {
          return { error: `${field.name} has no option named "${raw}".` }
        }
        value.optionID = option.id
        return { value }
      }

      case ECustomFieldTypes.Date: {
        const date = this.parseDate(raw)
        if (!date) {
          return {
            error: `${field.name} must be a date in ${DATE_FORMATS} format.`,
          }
        }
        value.dateData = date
        return { value }
      }

      case ECustomFieldTypes.Checkbox: {
        const answer = raw.toLowerCase()
        if (CHECKBOX_CHECKED_VALUES.includes(answer)) {
          value.checkboxData = true
          return { value }
        }
        if (CHECKBOX_UNCHECKED_VALUES.includes(answer)) {
          return {}
        }
        return { error: `${field.name} must be Yes or No.` }
      }

      default:
        return {}
    }
  }

  /**
   * Spreadsheets save formatted numbers as shown, e.g. "$1,200.50" or "25%", so the
   * field's label and thousands separators are dropped before the number is read.
   */
  private static stripNumberFormatting(field: ICustomField, raw: string): string {
    // A label can be digits, e.g. "1", so a cell that already reads as a number keeps every digit
    const number = this.ungroupNumber(raw)
    if (number !== null) {
      return number
    }

    let text = raw
    const isNegative = text.startsWith('-')
    if (isNegative) {
      text = text.slice(1).trim()
    }

    const labels = [(field.numberLabel || '').trim()]
    if (field.numberType === ECustomFieldNumberTypes.Percentage) {
      labels.push('%')
    }
    if (field.numberType === ECustomFieldNumberTypes.Currency) {
      labels.push('$')
    }
    for (const label of labels.filter(l => l)) {
      if (text.startsWith(label)) {
        text = text.slice(label.length).trim()
      }
      if (text.endsWith(label)) {
        text = text.slice(0, -label.length).trim()
      }
    }

    text = this.ungroupNumber(text) ?? text

    return isNegative ? `-${text}` : text
  }

  /* A plain number as is, or one grouped like 1,234.5 without its commas; null for anything else */
  private static ungroupNumber(text: string): string | null {
    if (/^-?(\d+(\.\d*)?|\.\d+)$/.test(text)) {
      return text
    }
    if (/^-?\d{1,3}(,\d{3})+(\.\d+)?$/.test(text)) {
      return text.replace(/,/g, '')
    }
    return null
  }

  /*
   * Reads YYYY-MM-DD, MM/DD/YYYY, or the exports' MMM D, YYYY (e.g. "Nov 5, 2026") into
   * YYYY-MM-DD, or null when it isn't a real date
   */
  private static parseDate(raw: string): string | null {
    let year: number
    let month: number
    let day: number

    const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(raw)
    const us = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(raw)
    const words = /^([a-z]{3}) (\d{1,2}), (\d{4})$/i.exec(raw)
    if (iso) {
      year = Number(iso[1])
      month = Number(iso[2])
      day = Number(iso[3])
    } else if (us) {
      year = Number(us[3])
      month = Number(us[1])
      day = Number(us[2])
    } else if (words) {
      year = Number(words[3])
      month = MONTH_ABBREVIATIONS.indexOf(words[1].toLowerCase()) + 1
      day = Number(words[2])
    } else {
      return null
    }

    const date = new Date(Date.UTC(year, month - 1, day))
    if (
      date.getUTCFullYear() !== year ||
      date.getUTCMonth() !== month - 1 ||
      date.getUTCDate() !== day
    ) {
      return null
    }

    const pad = (n: number) => String(n).padStart(2, '0')
    return `${year}-${pad(month)}-${pad(day)}`
  }

  /* What a custom field column should hold, for the format notes */
  public static describeCustomFieldFormat(field: ICustomField): string {
    switch (field.customFieldType) {
      case ECustomFieldTypes.Text:
        switch (field.textType) {
          case ECustomFieldTextTypes.Email:
            return 'Email address'
          case ECustomFieldTextTypes.PhoneNumber:
            return 'Phone number'
          case ECustomFieldTextTypes.Url:
            return 'URL starting with http:// or https://'
          default:
            return 'Text'
        }
      case ECustomFieldTypes.Number:
        return field.numberType === ECustomFieldNumberTypes.Percentage
          ? 'Number (e.g. 25 for 25%)'
          : 'Number'
      case ECustomFieldTypes.Dropdown: {
        const names = (field.options || []).map(o => `"${o.name}"`)
        if (names.length > MAX_OPTIONS_IN_FORMAT) {
          const more = names.length - MAX_OPTIONS_IN_FORMAT
          return `One of ${names
            .slice(0, MAX_OPTIONS_IN_FORMAT)
            .join(', ')}, or ${more} more`
        }
        return `One of ${names.join(', ')}`
      }
      case ECustomFieldTypes.Date:
        return `Date in ${DATE_FORMATS} format`
      case ECustomFieldTypes.Checkbox:
        return 'Yes or No'
      default:
        return ''
    }
  }

  /**
   * Maps a validated CSV row to the standard task create input format.
   * Resolves string names to IDs using the provided filter options.
   */
  public static mapCsvRowToCreateInput(
    row: ICsvTaskRow,
    options: ICsvImportOptions,
  ): ITasksInputCreate {
    // Resolve status — default to first active status if no match
    const statusMatch = options.listStatuses.find(
      s => s.name.toLowerCase().trim() === row.status.toLowerCase().trim(),
    )
    const statusID = statusMatch
      ? statusMatch.id
      : options.listStatuses[0].id

    // Resolve priority — default to "Low" if no match
    const priorityMatch = options.listPriorities.find(
      p => p.name.toLowerCase().trim() === row.priority.toLowerCase().trim(),
    )
    const lowPriority = options.listPriorities.find(
      p => p.name.toLowerCase() === 'low',
    )
    const priorityID = priorityMatch
      ? priorityMatch.id
      : lowPriority
        ? lowPriority.id
        : options.listPriorities[0].id

    // Resolve assignee — skip if not a valid project member
    let assigneeID: number | undefined
    if (row.assignee) {
      const assigneeMatch = options.listAssignees.find(
        a => a.email.toLowerCase().trim() === row.assignee!.toLowerCase().trim(),
      )
      if (assigneeMatch) {
        assigneeID = assigneeMatch.id
      }
    }

    // Parse tags
    const listTags = row.tags
      ? row.tags.split(',').map(t => t.trim()).filter(t => t !== '')
      : []

    let htmlDescription = row.description
    if (htmlDescription && !htmlDescription.startsWith('<') && !htmlDescription.endsWith('>')) {
      htmlDescription = `<p>${htmlDescription}</p>`
    }

    return {
      title: row.title,
      description: htmlDescription,
      assigneeID,
      listJobAppointeeMappings: [],
      statusID,
      priorityID,
      dueDate: row.dueDate || undefined,
      listTags,
      listWatchersIDs: [],
      listAtMentionIDs: [],
      listFileUploadIDs: [],
      listLinkedTaskIDs: [],
      listCustomFieldValues: row.customFieldValues,
    }
  }

  /**
   * Returns a downloadable example CSV string, with an empty column for each custom field.
   * Pass the same fields, in the same order, that validateCsvStructure will match the file against.
   */
  public static generateExampleCsvContent(
    customFields: ICustomField[] = [],
  ): string {
    const customFieldHeaders = customFields
      .map(f => `,"${f.name.replace(/"/g, '""')}"`)
      .join('')
    const customFieldCells = customFields.map(() => ',""').join('')
    return [
      'title,description,status,priority,assignee,due_date,tags' +
        customFieldHeaders,
      '"Fix login bug","Users cannot log in with SSO","Open","High","jane@example.com","2026-04-15","bug,authentication"' +
        customFieldCells,
      '"Update docs","Add API reference for v2","Open","Low","","","documentation"' +
        customFieldCells,
      '"Design review","Review new dashboard mockups","In Progress","Medium","john@example.com","2026-04-01","design,review"' +
        customFieldCells,
    ].join('\n')
  }
}
